import {
  CircleGeometry,
  Color,
  Mesh,
  RepeatWrapping,
  ShaderMaterial,
  SRGBColorSpace,
  TextureLoader,
  Uniform,
} from "three";
import type { Texture } from "three";
import {
  DEFAULT_UNDERWATER_VISUAL_SETTINGS,
  type UnderwaterVisualSettings,
} from "./VisualSettings";

const vertexShader = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vWorldPosition;
  varying vec3 vViewPosition;

  void main() {
    vUv = uv;
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vec4 viewPosition = viewMatrix * worldPosition;
    vWorldPosition = worldPosition.xyz;
    vViewPosition = viewPosition.xyz;
    gl_Position = projectionMatrix * viewPosition;
  }
`;

const SEABED_SAND_TEXTURE_URL = "/textures/aquarium-sand-tile.webp";
let sharedSandTexture: Texture | null = null;

export function getSeabedSandTexture() {
  if (!sharedSandTexture) {
    sharedSandTexture = new TextureLoader().load(SEABED_SAND_TEXTURE_URL);
    sharedSandTexture.wrapS = RepeatWrapping;
    sharedSandTexture.wrapT = RepeatWrapping;
    sharedSandTexture.colorSpace = SRGBColorSpace;
    sharedSandTexture.anisotropy = 4;
  }

  return sharedSandTexture;
}

let sharedDirtTexture: Texture | null = null;

export function getDirtTexture() {
  if (!sharedDirtTexture) {
    sharedDirtTexture = new TextureLoader().load('/textures/dirt_particles.png');
    sharedDirtTexture.wrapS = RepeatWrapping;
    sharedDirtTexture.wrapT = RepeatWrapping;
    sharedDirtTexture.colorSpace = SRGBColorSpace;
    sharedDirtTexture.anisotropy = 4;
  }

  return sharedDirtTexture;
}

const fragmentShader = /* glsl */ `
  #define TAU 6.28318530718
  #define MAX_CAUSTIC_ITER 3
  uniform float time;
  uniform float opacity;
  uniform float murkiness;
  uniform float textureScale;
  uniform float caustics;
  uniform float causticsScale;
  uniform float causticsSpeed;
  uniform float causticsIntensity;
  uniform float relief;
  uniform vec3 causticsHighlightColor;
  uniform sampler2D sandMap;
  varying vec2 vUv;
  varying vec3 vWorldPosition;
  varying vec3 vViewPosition;

  float waterCaustics(vec2 uv, float anim) {
    vec2 p = mod(uv * TAU, TAU) - 250.0;
    vec2 i = vec2(p);
    float c = 1.0;
    float inten = 0.005;

    for (int n = 0; n < MAX_CAUSTIC_ITER; n++) {
      float t = anim * (1.0 - (3.5 / float(n + 1)));
      i = p + vec2(
        cos(t - i.x) + sin(t + i.y),
        sin(t - i.y) + cos(t + i.x)
      );
      c += 1.0 / length(vec2(
        p.x / (sin(i.x + t) / inten),
        p.y / (cos(i.y + t) / inten)
      ));
    }

    c /= float(MAX_CAUSTIC_ITER);
    c = 1.17 - pow(c, 1.4);
    return pow(abs(c), 4.4);
  }

  void main() {
    vec2 centered = vUv - vec2(0.5);
    float radial = length(centered) * 2.0;
    float edgeFade = 1.0 - smoothstep(0.76, 1.0, radial);
    float centerFalloff = 1.0 - smoothstep(0.04, 1.12, radial);
    float outerShadow = smoothstep(0.35, 1.0, radial);
    float viewDepth = smoothstep(2.4, 7.6, -vViewPosition.z);
    float scale = max(textureScale, 0.05);
    vec2 floorPosition = vWorldPosition.xz;
    vec2 p = floorPosition * 1.35 * scale;
    vec2 sandUv = floorPosition * 0.82 * scale + vec2(0.07, -0.11);
    vec3 sandTexel = texture2D(sandMap, sandUv).rgb;
    float grain = dot(sandTexel, vec3(0.299, 0.587, 0.114));
    float broadSlope = floorPosition.y * 4.1 + floorPosition.x * 0.85;
    float bands = sin(broadSlope * scale + 2.8) * 0.5 + 0.5;
    float ridge = smoothstep(0.52, 0.72, bands) - smoothstep(0.78, 0.98, bands);
    float trough = 1.0 - smoothstep(0.16, 0.36, bands);
    float ripples = sin((floorPosition.x * 3.3 + floorPosition.y * 0.7) * scale + 4.0) * 0.5 + 0.5;
    float rippleCaustic = pow(ripples, 5.0);
    float causticScale = max(causticsScale, 0.05);
    float causticAnim = time * causticsSpeed + 23.0;
    float projectedCaustic = waterCaustics(
      (floorPosition + vec2(0.08, time * causticsSpeed * 0.06)) *
        causticScale,
      causticAnim
    );
    float caustic =
      (projectedCaustic * 1.35 + rippleCaustic * 0.35) *
      caustics *
      causticsIntensity;
    float reliefAmount = max(relief, 0.0);

    vec3 deep = vec3(0.045, 0.13, 0.135) * murkiness;
    vec3 wetTint = vec3(0.64, 0.72, 0.62);
    vec3 warmSand = vec3(0.86, 0.68, 0.38);
    vec3 paleShell = vec3(1.0, 0.9, 0.62);
    vec3 sandColor = mix(warmSand, paleShell, smoothstep(0.3, 0.82, grain));
    sandColor *= sandTexel * vec3(1.28, 1.16, 0.82);

    vec3 color = mix(sandColor, wetTint, 0.05 * murkiness + 0.035);
    color += vec3(0.18, 0.13, 0.055) * (grain - 0.46) * reliefAmount;
    color += vec3(0.13, 0.10, 0.045) * 0.5 * reliefAmount * 0.26;
    color += vec3(0.16, 0.13, 0.055) * ridge * reliefAmount * 0.18;
    color -= vec3(0.15, 0.10, 0.055) * trough * reliefAmount * 0.18;
    color += causticsHighlightColor * caustic * edgeFade;
    color = mix(color, deep, outerShadow * 0.14 + viewDepth * 0.18);

    float alpha = opacity * edgeFade * (0.88 + centerFalloff * 0.12);
    if (alpha < 0.015) {
      discard;
    }
    color = mix(deep, color, clamp(alpha, 0.0, 1.0));
    gl_FragColor = vec4(color, 1.0);
  }
`;

export class Seabed extends Mesh {
  private readonly timeUniform: Uniform<number>;
  private readonly opacityUniform: Uniform<number>;
  private readonly murkinessUniform: Uniform<number>;
  private readonly textureScaleUniform: Uniform<number>;
  private readonly causticsUniform: Uniform<number>;
  private readonly causticsScaleUniform: Uniform<number>;
  private readonly causticsSpeedUniform: Uniform<number>;
  private readonly causticsIntensityUniform: Uniform<number>;
  private readonly causticsHighlightColorUniform: Uniform<Color>;
  private readonly reliefUniform: Uniform<number>;

  constructor(roomSize: number, baseCameraHeight: number) {
    const geometry = new CircleGeometry(roomSize, 128);
    const material = new ShaderMaterial({
      transparent: false,
      depthWrite: true,
      depthTest: true,
      uniforms: {
        time: new Uniform(0),
        opacity: new Uniform(DEFAULT_UNDERWATER_VISUAL_SETTINGS.floorOpacity),
        murkiness: new Uniform(
          DEFAULT_UNDERWATER_VISUAL_SETTINGS.seabedMurkiness
        ),
        textureScale: new Uniform(
          DEFAULT_UNDERWATER_VISUAL_SETTINGS.seabedTextureScale
        ),
        caustics: new Uniform(DEFAULT_UNDERWATER_VISUAL_SETTINGS.seabedCaustics),
        causticsScale: new Uniform(
          DEFAULT_UNDERWATER_VISUAL_SETTINGS.causticsScale
        ),
        causticsSpeed: new Uniform(
          DEFAULT_UNDERWATER_VISUAL_SETTINGS.causticsSpeed
        ),
        causticsIntensity: new Uniform(
          DEFAULT_UNDERWATER_VISUAL_SETTINGS.causticsIntensity
        ),
        causticsHighlightColor: new Uniform(
          new Color(DEFAULT_UNDERWATER_VISUAL_SETTINGS.causticsHighlightColor)
        ),
        relief: new Uniform(DEFAULT_UNDERWATER_VISUAL_SETTINGS.seabedRelief),
        sandMap: new Uniform(getSeabedSandTexture()),
      },
      vertexShader,
      fragmentShader,
    });

    super(geometry, material);

    this.timeUniform = material.uniforms.time as Uniform<number>;
    this.opacityUniform = material.uniforms.opacity as Uniform<number>;
    this.murkinessUniform = material.uniforms.murkiness as Uniform<number>;
    this.textureScaleUniform = material.uniforms.textureScale as Uniform<number>;
    this.causticsUniform = material.uniforms.caustics as Uniform<number>;
    this.causticsScaleUniform = material.uniforms
      .causticsScale as Uniform<number>;
    this.causticsSpeedUniform = material.uniforms
      .causticsSpeed as Uniform<number>;
    this.causticsIntensityUniform = material.uniforms
      .causticsIntensity as Uniform<number>;
    this.causticsHighlightColorUniform = material.uniforms
      .causticsHighlightColor as Uniform<Color>;
    this.reliefUniform = material.uniforms.relief as Uniform<number>;
    this.name = "seabed";
    this.renderOrder = -50;
    this.rotation.set(-Math.PI / 2, 0, 0);
    this.position.set(0, -0.28 - baseCameraHeight, 0);
  }

  setSettings(settings: UnderwaterVisualSettings, baseCameraHeight: number) {
    this.visible = settings.floorOpacity > 0;
    this.opacityUniform.value = settings.floorOpacity;
    this.murkinessUniform.value = settings.seabedMurkiness;
    this.textureScaleUniform.value = settings.seabedTextureScale;
    this.causticsUniform.value = settings.seabedCaustics;
    this.causticsScaleUniform.value = settings.causticsScale;
    this.causticsSpeedUniform.value = settings.causticsSpeed;
    this.causticsIntensityUniform.value = settings.causticsIntensity;
    this.causticsHighlightColorUniform.value.set(
      settings.causticsHighlightColor
    );
    this.reliefUniform.value = settings.seabedRelief;
    this.scale.setScalar(settings.seabedRadius);
    this.position.y = -0.28 - baseCameraHeight - settings.seabedDepth;
  }

  update(time: number) {
    this.timeUniform.value = time;
  }
}
